import { useEffect, useMemo, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import {
  Database,
  Table2,
  Columns3,
  Search,
  Eye,
  X,
  CheckCircle2,
  AlertTriangle,
  Layers,
} from "lucide-react";

const MODULE_LABELS = {
  po_sales: "PO / Sales",
  production: "Production",
  qa: "QA / Lab",
  procurement: "Procurement",
  purchase: "Purchase",
  stock: "Store / Stock",
  shipment: "Dispatch / Shipment",
  finance: "Finance / Payments",
  master: "Master Data",
  hr_admin: "Admin / Utility",
  unmapped: "Unmapped",
};

export default function DatabaseExplorer() {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ summary: {}, tables: [] });
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/database-explorer/summary", {
        params: {
          search: search || undefined,
          module: module || undefined,
          status: status || undefined,
        },
      });
      setPayload(r.data || { summary: {}, tables: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modules = useMemo(() => {
    const m = payload.summary?.modules || {};
    return Object.keys(m).sort((a, b) => String(a).localeCompare(String(b)));
  }, [payload]);

  const openTable = async (table) => {
    setDetailLoading(true);
    setSelected({ table_name: table.table_name, loading: true });

    try {
      const r = await api.get(`/database-explorer/tables/${encodeURIComponent(table.table_name)}`, {
        params: { connection_id: table.connection_id },
      });
      setSelected(r.data);
    } finally {
      setDetailLoading(false);
    }
  };

  const summary = payload.summary || {};
  const tables = payload.tables || [];

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Database Explorer</h1>
            <p>
              Clear SQL database, tables, columns, row counts, module mapping, and planning view
              before building CEO/CIO Command Center dashboards.
            </p>
          </div>

          <button className="erp-primary-btn" onClick={load} disabled={loading}>
            <Database size={15} strokeWidth={1.8} />
            {loading ? "Refreshing..." : "Refresh Catalog"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Tables" value={summary.tables || 0} icon={Table2} />
          <Stat label="Columns" value={summary.columns || 0} icon={Columns3} />
          <Stat label="Active Tables" value={summary.active_tables || 0} icon={CheckCircle2} />
          <Stat label="Skipped Tables" value={summary.skipped_tables || 0} icon={AlertTriangle} />
        </div>

        <div className="erp-card mb-4">
          <div className="database-filter-row">
            <div className="database-search">
              <Search size={15} strokeWidth={1.8} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search table, column, keyword, module..."
              />
            </div>

            <select value={module} onChange={(e) => setModule(e.target.value)}>
              <option value="">All modules</option>
              {modules.map((m) => (
                <option key={m} value={m}>
                  {MODULE_LABELS[m] || m} ({payload.summary?.modules?.[m] || 0})
                </option>
              ))}
            </select>

            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All status</option>
              <option value="active_candidate">Active candidate</option>
              <option value="skipped">Skipped</option>
            </select>

            <button className="erp-action-btn small-action" onClick={load}>
              Apply Filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <div className="xl:col-span-8 erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>SQL Tables</h2>
                <p>{tables.length} tables shown from ERP catalog. Click any row to view columns and planning details.</p>
              </div>
            </div>

            <div className="database-table-wrap">
              <table className="tbl database-table" data-testid="database-explorer-table">
                <thead>
                  <tr>
                    <th>Table</th>
                    <th>Module</th>
                    <th>Rows</th>
                    <th>Columns</th>
                    <th>Key Fields</th>
                    <th>Date Fields</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {tables.map((t) => (
                    <tr
                      key={`${t.connection_id}-${t.table_name}`}
                      className="database-clickable-row"
                      onClick={() => openTable(t)}
                      title="Click to view table columns and planning details"
                    >
                      <td>
                        <strong className="font-mono">{t.table_name}</strong>
                        <div className="database-muted">{t.database}</div>
                      </td>
                      <td>
                        <span className="database-module-chip">
                          {MODULE_LABELS[t.module_guess] || t.module_guess || "Unmapped"}
                        </span>
                      </td>
                      <td className="numeric">{Number(t.row_count || 0).toLocaleString()}</td>
                      <td className="numeric">{t.column_count}</td>
                      <td className="database-small-list">{(t.key_fields || []).slice(0, 3).join(", ") || "—"}</td>
                      <td className="database-small-list">{(t.date_fields || []).slice(0, 3).join(", ") || "—"}</td>
                      <td>
                        <span className={`database-status ${t.status === "skipped" ? "skipped" : "active"}`}>
                          {t.status === "skipped" ? "Skipped" : "Active"}
                        </span>
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          className="btn btn-outline text-[11px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            openTable(t);
                          }}
                        >
                          <Eye size={12} strokeWidth={1.8} /> View
                        </button>
                      </td>
                    </tr>
                  ))}

                  {tables.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>
                        No tables found for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="xl:col-span-4 erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Planning View</h2>
                <p>Use this before building dashboards and reports.</p>
              </div>
              <Layers size={18} strokeWidth={1.8} />
            </div>

            {!selected && (
              <div className="database-empty-detail">
                Select a table to review columns, business fields, and dashboard/report usage.
              </div>
            )}

            {selected && (
              <div className="database-detail">
                <div className="database-detail-head">
                  <div>
                    <h3>{selected.table_name}</h3>
                    <p>{selected.database}</p>
                  </div>
                  <button className="btn btn-ghost" onClick={() => setSelected(null)}>
                    <X size={14} strokeWidth={1.8} />
                  </button>
                </div>

                {detailLoading ? (
                  <div className="database-empty-detail">Loading table details...</div>
                ) : (
                  <>
                    <div className="database-detail-grid">
                      <Info label="Module" value={MODULE_LABELS[selected.module_guess] || selected.module_guess || "Unmapped"} />
                      <Info label="Rows" value={Number(selected.row_count || 0).toLocaleString()} />
                      <Info label="Columns" value={(selected.columns || []).length} />
                      <Info label="Status" value={selected.status || "—"} />
                    </div>

                    <FieldGroup title="Date Fields" items={selected.business_fields?.date_fields} />
                    <FieldGroup title="Amount / Value Fields" items={selected.business_fields?.amount_fields} />
                    <FieldGroup title="Quantity / Weight Fields" items={selected.business_fields?.quantity_fields} />
                    <FieldGroup title="ID Fields" items={selected.business_fields?.id_fields} />
                    <FieldGroup title="Name / Code Fields" items={selected.business_fields?.name_fields} />
                    <FieldGroup title="Status Fields" items={selected.business_fields?.status_fields} />

                    <div className="database-columns-box">
                      <h4>All Columns</h4>
                      {(selected.columns || []).map((c) => (
                        <div key={c.name} className="database-column-row">
                          <strong>{c.name}</strong>
                          <span>{c.data_type}</span>
                          <small>{c.key || c.nullable || ""}</small>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="erp-stat-card">
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>ERP catalog</small>
      </div>
      <div className="erp-stat-icon">
        <Icon size={19} strokeWidth={1.8} />
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="database-info">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function FieldGroup({ title, items }) {
  const list = items || [];
  return (
    <div className="database-field-group">
      <h4>{title}</h4>
      {list.length === 0 ? (
        <p>Not detected</p>
      ) : (
        <div>
          {list.slice(0, 12).map((x) => <span key={x}>{x}</span>)}
        </div>
      )}
    </div>
  );
}
